/**
 * Utilidades para separar marcadores Leaflet superpuestos (spiderfy local).
 * @module utils/mapMarkerSpiderfy
 */

import L from 'leaflet'
import type { AnyNode } from '@/types/nodes'

/** Clave estable para nodos en la misma posición (≈11 cm con 6 decimales). */
export function nodePositionKey(lat: number, lng: number, decimals = 6): string {
  return `${lat.toFixed(decimals)},${lng.toFixed(decimals)}`
}

/** Agrupa ids de nodos visibles que comparten la misma clave de posición. */
export function groupVisibleNodeIdsByPosition(
  nodes: AnyNode[],
  visibleNodeTypes: Set<string>
): Map<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const node of nodes) {
    if (!visibleNodeTypes.has(node.tipo)) continue
    const key = nodePositionKey(node.lat, node.lng)
    const list = groups.get(key)
    if (list) list.push(node.id)
    else groups.set(key, [node.id])
  }
  for (const ids of groups.values()) {
    ids.sort()
  }
  return groups
}

/**
 * Posiciones en círculo alrededor del centro (espacio de píxeles del mapa).
 * El primer punto queda al norte del centro.
 */
export function computeSpiderfyLatLngs(
  map: L.Map,
  center: L.LatLng,
  count: number,
  radiusPx: number
): L.LatLng[] {
  if (count <= 0) return []
  if (count === 1) return [L.latLng(center)]
  const centerPt = map.latLngToLayerPoint(center)
  const out: L.LatLng[] = []
  for (let i = 0; i < count; i++) {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / count
    const x = centerPt.x + radiusPx * Math.cos(angle)
    const y = centerPt.y + radiusPx * Math.sin(angle)
    out.push(map.layerPointToLatLng(L.point(x, y)))
  }
  return out
}

export type SpiderfiedState = {
  groupKey: string
  originals: Map<string, L.LatLng>
  legLayers: L.Layer[]
}

export function collapseSpiderfy(
  map: L.Map,
  state: SpiderfiedState | null,
  markersById: Map<string, L.Marker>
): void {
  if (!state) return
  for (const [id, latlng] of state.originals) {
    const m = markersById.get(id)
    if (m) m.setLatLng(latlng)
  }
  for (const layer of state.legLayers) {
    map.removeLayer(layer)
  }
}

export function expandSpiderfy(
  map: L.Map,
  markersById: Map<string, L.Marker>,
  groupIds: string[],
  groupKey: string,
  radiusPx: number
): SpiderfiedState | null {
  if (groupIds.length < 2) return null

  const originals = new Map<string, L.LatLng>()
  const firstMarker = markersById.get(groupIds[0])
  if (!firstMarker) return null
  const center = firstMarker.getLatLng()

  for (const id of groupIds) {
    const m = markersById.get(id)
    if (!m) return null
    originals.set(id, L.latLng(m.getLatLng()))
    m.closePopup()
    m.closeTooltip()
  }

  const positions = computeSpiderfyLatLngs(map, center, groupIds.length, radiusPx)
  groupIds.forEach((id, i) => {
    markersById.get(id)?.setLatLng(positions[i])
  })

  const legLayers: L.Layer[] = []
  for (let i = 0; i < groupIds.length; i++) {
    const line = L.polyline([center, positions[i]], {
      weight: 1,
      opacity: 0.55,
      dashArray: '4 6',
      interactive: false,
      className: 'neteasy-spiderfy-leg',
    })
    line.addTo(map)
    legLayers.push(line)
  }

  return { groupKey, originals, legLayers }
}
